/**
 * Data Access Layer for Completion Notification Roles
 * Manages which participant roles should be notified when an entity is completed
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import type {
  CompletionNotificationRole,
  CompletionNotificationRoleCreate,
  CompletionNotificationEntityType,
} from './types.js';

/**
 * Get all notification roles for an entity
 */
export async function getCompletionNotificationRoles(
  entityType: CompletionNotificationEntityType,
  entityId: string
): Promise<CompletionNotificationRole[]> {
  const { data, error } = await getClient()
    .from(TABLES.completion_notification_roles)
    .select('*')
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .order('notify_role');

  if (error) throw error;
  return (data || []) as CompletionNotificationRole[];
}

/**
 * Set notification roles for an entity (replaces all existing)
 */
export async function setCompletionNotificationRoles(
  entityType: CompletionNotificationEntityType,
  entityId: string,
  roles: string[],
  accountId: string
): Promise<CompletionNotificationRole[]> {
  // First, delete all existing roles for this entity
  const { error: deleteError } = await getClient()
    .from(TABLES.completion_notification_roles)
    .delete()
    .eq('entity_type', entityType)
    .eq('entity_id', entityId);

  if (deleteError) throw deleteError;

  // If no roles to add, return empty array
  if (roles.length === 0) {
    return [];
  }

  // Insert new roles
  const inserts: CompletionNotificationRoleCreate[] = roles.map((role) => ({
    entity_type: entityType,
    entity_id: entityId,
    notify_role: role,
    account_id: accountId,
  }));

  const { data, error: insertError } = await getClient()
    .from(TABLES.completion_notification_roles)
    .insert(inserts)
    .select();

  if (insertError) throw insertError;
  return (data || []) as CompletionNotificationRole[];
}

/**
 * Add a single notification role for an entity
 */
export async function addCompletionNotificationRole(
  input: CompletionNotificationRoleCreate
): Promise<CompletionNotificationRole> {
  const { data, error } = await getClient()
    .from(TABLES.completion_notification_roles)
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data as CompletionNotificationRole;
}

/**
 * Remove a single notification role by ID
 */
export async function removeCompletionNotificationRole(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.completion_notification_roles)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Delete all notification roles for an entity
 */
export async function deleteCompletionNotificationRoles(
  entityType: CompletionNotificationEntityType,
  entityId: string
): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.completion_notification_roles)
    .delete()
    .eq('entity_type', entityType)
    .eq('entity_id', entityId);

  if (error) throw error;
}

/**
 * Check if any notification roles are configured for an entity
 */
export async function hasCompletionNotificationRoles(
  entityType: CompletionNotificationEntityType,
  entityId: string
): Promise<boolean> {
  const { count, error } = await getClient()
    .from(TABLES.completion_notification_roles)
    .select('*', { count: 'exact', head: true })
    .eq('entity_type', entityType)
    .eq('entity_id', entityId);

  if (error) throw error;
  return (count ?? 0) > 0;
}

/**
 * Get just the role names for an entity
 */
export async function getCompletionNotificationRoleNames(
  entityType: CompletionNotificationEntityType,
  entityId: string
): Promise<string[]> {
  const roles = await getCompletionNotificationRoles(entityType, entityId);
  return roles.map((r) => r.notify_role);
}
